import { type SendingDomain, domainCreateSchema } from "@mailhand/core/schemas";
import { type SubmitEvent, useState } from "react";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { usePoll } from "../../lib/usePoll.js";
import { Badge, Button, Card, CardHeader, CopyButton, Field, Input } from "../../ui/index.js";

const DOMAIN_POLL_MS = 15_000;

export function DomainsSection() {
  const api = useApi();
  const { view, setView } = useTenant();
  const task = useTask();
  const [domain, setDomain] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const pending = view.domains.filter((candidate) => candidate.state !== "ready");

  usePoll(
    async () => {
      const fresh = await Promise.all(pending.map((candidate) => api.call("domainGet", { params: { domain: candidate.domain } })));
      setView({ ...view, domains: view.domains.map((existing) => fresh.find((candidate) => candidate.domain === existing.domain) ?? existing) });
    },
    DOMAIN_POLL_MS,
    pending.length > 0,
    false,
  );

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const parsed = domainCreateSchema.safeParse({ domain });
    if (!parsed.success) {
      setError("Type a domain like acmecoffee.com, without https:// or www.");
      return;
    }
    setError(undefined);
    const ok = await task.run(async () => {
      const created = await api.call("domainCreate", { body: parsed.data });
      setView({ ...view, domains: [...view.domains, created] });
    });
    if (ok) setDomain("");
  }

  return (
    <Card id="domain">
      <CardHeader eyebrow="Email Domain Settings" />
      <p className="small muted">Only replies change. Your mailbox stays where it is.</p>
      <div className="stack-lg">
        {view.domains.map((sendingDomain) => (
          <DomainRecords key={sendingDomain.domain} domain={sendingDomain} />
        ))}
        <form className="inline-form" onSubmit={(event) => void submit(event)} noValidate>
          <Field label="Domain" error={error}>
            {({ id, describedBy, invalid }) => (
              <Input id={id} className="mono" placeholder="acmecoffee.com" value={domain} aria-describedby={describedBy} aria-invalid={invalid} onChange={(event) => { setDomain(event.target.value); }} />
            )}
          </Field>
          <Button type="submit" busy={task.busy}>
            Add domain
          </Button>
        </form>
      </div>
    </Card>
  );
}

function DomainRecords({ domain }: { domain: SendingDomain }) {
  const api = useApi();
  const { view, setView } = useTenant();
  const task = useTask();
  const [confirming, setConfirming] = useState(false);

  async function remove(): Promise<void> {
    await task.run(async () => {
      await api.call("domainDelete", { params: { domain: domain.domain } });
      setView({ ...view, domains: view.domains.filter((existing) => existing.domain !== domain.domain) });
    }, `Removed ${domain.domain}. Its inboxes use the shared address again.`);
  }

  return (
    <div className="subcard stack">
      <div className="subcard-head">
        <strong className="mono">{domain.domain}</strong>
        <Badge tone={domain.state === "ready" ? "green" : "amber"} dot>
          {domain.state === "ready" ? "Ready" : "Waiting for DNS"}
        </Badge>
      </div>
      <DomainStep domain={domain} />
      <div className="records">
        {domain.dnsRecords.map((record) => (
          <div key={record.name} className="record">
            <span className="record-type mono">{record.type}</span>
            <div className="record-field">
              <span className="record-label">Name</span>
              <span className="mono record-value">{record.name}</span>
              <CopyButton value={record.name} />
            </div>
            <div className="record-field">
              <span className="record-label">Value</span>
              <span className="mono record-value">{record.value}</span>
              <CopyButton value={record.value} />
            </div>
          </div>
        ))}
      </div>
      <div className="button-row">
        {confirming ? (
          <>
            <Button size="sm" variant="danger" busy={task.busy} onClick={() => void remove()}>
              Yes, remove {domain.domain}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setConfirming(false); }}>
              Keep it
            </Button>
          </>
        ) : (
          <Button size="sm" variant="ghost" onClick={() => { setConfirming(true); }}>
            Remove domain
          </Button>
        )}
      </div>
    </div>
  );
}

function DomainStep({ domain }: { domain: SendingDomain }) {
  switch (domain.state) {
    case "pending_challenge":
      return (
        <p className="small muted">
          Step 1 of 2: add this TXT record at your DNS host to prove the domain is yours. This page checks every few seconds. Add it before{" "}
          {new Date(domain.challengeExpiresAt).toLocaleString()}, or add the domain again.
        </p>
      );
    case "pending_dns":
      return <p className="small muted">Step 2 of 2: add these three CNAME records at your DNS host. This page checks every few seconds; DNS can take up to a day.</p>;
    case "ready":
      return <p className="small muted">Pick an address at this domain under Inboxes → Send replies from.</p>;
  }
}
